import React from 'react';
import PropTypes from 'prop-types';
import {withRouter, locationShape, routerShape} from 'react-router';
import getFormData from 'get-form-data';
import autobind from 'autobind-decorator';

const {toString} = Object.prototype;

const typeOf = obj => toString.call(obj).slice(8, -1).toLowerCase();

function createLocationDescriptor({to, query, state}) {
  if (typeOf(to) === 'string') {
    return {pathname: to, query, state};
  }
  return {query, state, ...to};
}

/**
 * <Form> components are used to create a <form> element that submits its input
 * data to a route.
 *
 * For example, assuming you have the following route:
 *
 *   <Route path="/topics/:topicId/add-post" onEnter={handleAddPost}/>
 *
 * You could use the following component to submit a form's input data to that
 * route as location state:
 *
 *   <Form to={`/topics/${topicId}/add-post`} method="POST">
 */
@withRouter
class Form extends React.Component {
  static propTypes = {
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,

    component: PropTypes.any,
    dataKey: PropTypes.string,
    extractFormData: PropTypes.func,
    method: PropTypes.string,
    methodKey: PropTypes.string,
    onSubmit: PropTypes.func,
    query: PropTypes.object,
    state: PropTypes.object,
    disabled: PropTypes.bool,
    onValidationFail: PropTypes.func,
    checkValidity: PropTypes.func,


    router: routerShape.isRequired,
    location: locationShape,
    routes: PropTypes.any,
    params: PropTypes.any,
  };

  static defaultProps = {
    component: 'form',
    dataKey: 'body',
    extractFormData: getFormData,
    method: 'GET',
    methodKey: 'method',
  };

  static childContextTypes = {
    formHasSubmitted: PropTypes.bool.isRequired,
  };

  state = {formHasSubmitted: false};

  getChildContext() {
    return this.state;
  }

  @autobind
  handleSubmit(event) {
    this.setState({formHasSubmitted: true});

    if (this.props.checkValidity) this.props.checkValidity();

    const formData = this.props.extractFormData(event.target);
    let allowTransition = true;
    let hasValidationError;
    let submitResult;

    if (this.props.onSubmit) {
      submitResult = this.props.onSubmit(event, formData);
    }

    if (
      submitResult === false ||
      this.props.disabled ||
      event.defaultPrevented === true
    ) {
      allowTransition = false;
    } else if (
      (event.target.checkValidity && !event.target.checkValidity()) ||
      (event.target.current && event.target.current.checkValidity && !event.target.current.checkValidity()) // added for new react.createRef refs have current property
    ) {
      allowTransition = false;
      hasValidationError = true;
      if (this.props.onValidationFail) {
        this.props.onValidationFail(event);
      }
    }

    event.preventDefault();

    if (allowTransition) {
      const {
        dataKey, method, methodKey, query, state, to,
      } = this.props;
      const submitLocation = createLocationDescriptor({to, query, state});

      if (method === 'GET') {
        // GET submissions use the query string, so just merge form data into it
        submitLocation.query = {...submitLocation.query, ...formData};
      } else {
        submitLocation.state = {
          ...submitLocation.state,
          handled: false,
          [methodKey]: method,
          [dataKey]: formData,
        };
      }
      this.props.router.push(submitLocation);
    } else if (hasValidationError) {
      // focus on first error
      if (typeof window !== 'undefined') {
        try {
          const input = this.el.querySelector('input:invalid,textarea:invalid');
          input.focus();
        } catch (error) {
          /* noop */
          if (__DEV__) { console.error(error); }
        }
      }
    }
  }

  @autobind elRef(el) { this.el = el; }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      component, dataKey, extractFormData,
      methodKey, onSubmit, query, state, to,
      router, location, params, routes, onValidationFail,
      checkValidity,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    rest.onSubmit = this.handleSubmit;

    if (router) {
      rest.action = router.createHref(createLocationDescriptor({to, query}));
    }

    return <this.props.component {...rest} noValidate ref={this.elRef} />;
  }
}

export default Form;



// WEBPACK FOOTER //
// ./spa/Components/Form/Form.jsx